import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const charVariants = {
  hidden: { y: 80, opacity: 0, rotateX: -40 },
  visible: {
    y: 0,
    opacity: 1,
    rotateX: 0,
    transition: { type: 'spring', damping: 14, stiffness: 120 },
  },
};

const AnimatedWord = ({ text, className, delayOffset = 0 }) => (
  <motion.span
    initial="hidden"
    animate="visible"
    variants={{
      hidden: {},
      visible: { transition: { staggerChildren: 0.045, delayChildren: 0.3 + delayOffset } },
    }}
    className={`inline-block ${className}`}
    aria-label={text}
  >
    {text.split('').map((char, i) => (
      <motion.span key={i} variants={charVariants} className="inline-block">
        {char === ' ' ? '\u00A0' : char}
      </motion.span>
    ))}
  </motion.span>
);

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: '#0a0f1e' }}>

      {/* ── Artistic background layer ── */}

      {/* Large orb top-left */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          top: '-18%',
          left: '-12%',
          width: '65vw',
          height: '65vw',
          maxWidth: 900,
          maxHeight: 900,
          background: 'radial-gradient(circle, rgba(109,40,217,0.22) 0%, rgba(109,40,217,0.06) 45%, transparent 70%)',
          filter: 'blur(8px)',
        }}
        animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Large orb bottom-right */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          bottom: '-20%',
          right: '-10%',
          width: '55vw',
          height: '55vw',
          maxWidth: 780,
          maxHeight: 780,
          background: 'radial-gradient(circle, rgba(6,182,212,0.18) 0%, rgba(6,182,212,0.05) 50%, transparent 72%)',
          filter: 'blur(8px)',
        }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.75, 1, 0.75] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      {/* Accent orb center */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          top: '30%',
          left: '38%',
          width: '35vw',
          height: '35vw',
          maxWidth: 500,
          maxHeight: 500,
          background: 'radial-gradient(circle, rgba(236,72,153,0.09) 0%, transparent 65%)',
          filter: 'blur(12px)',
        }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Diagonal ruled lines */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            -55deg,
            transparent,
            transparent 80px,
            rgba(148,163,184,0.028) 80px,
            rgba(148,163,184,0.028) 81px
          )`,
        }}
      />

      {/* Fine dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(148,163,184,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Horizontal accent line */}
      <div
        className="absolute left-0 right-0 pointer-events-none"
        style={{ top: '50%', height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(109,40,217,0.18) 30%, rgba(6,182,212,0.18) 70%, transparent 100%)' }}
      />

      {/* Noise grain */}
      <div className="absolute inset-0 opacity-[0.022] bg-noise pointer-events-none" />

      {/* ── Content ── */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-6 items-center">

          {/* Left column — text */}
          <div className="flex flex-col items-start">

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 text-violet-400 px-4 py-2 rounded-full text-sm font-medium mb-10"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Senior Quality Engineer · S&amp;P Global
            </motion.div>

            {/* Main headline — role/specialty instead of name */}
            <div className="mb-6 overflow-hidden" style={{ perspective: '800px' }}>
              <h1 className="font-display font-bold tracking-tight leading-[0.92] text-left" style={{ fontSize: 'clamp(2.4rem, 5.5vw, 5rem)' }}>
                <span className="block text-slate-100">
                  <AnimatedWord text="Quality" className="" delayOffset={0} />
                  {' '}
                  <AnimatedWord text="Engineer" className="" delayOffset={0.15} />
                </span>
                <span className="block" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  <AnimatedWord text="&" className="" delayOffset={0.35} />
                  {' '}
                  <AnimatedWord text="AI" className="" delayOffset={0.42} />
                  {' '}
                  <AnimatedWord text="Tester" className="" delayOffset={0.5} />
                </span>
              </h1>
            </div>

            {/* Thin divider */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: 'left', background: 'linear-gradient(90deg, rgba(139,92,246,0.8), rgba(6,182,212,0.5), transparent)' }}
              className="h-px w-24 mb-8"
            />

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="text-base text-slate-400 max-w-md leading-relaxed mb-10"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              QA Engineer specjalizujący się w testowaniu AI — ewaluacja LLM z{' '}
              <span className="text-cyan-400 font-medium">Langfuse</span>, budowanie agentów
              z <span className="text-cyan-400 font-medium">LangGraph</span> i integracje{' '}
              <span className="text-cyan-400 font-medium">MCP</span>. Python, JavaScript, AWS, CI/CD.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.3 }}
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <Link
                to="/projects"
                className="group inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/30 hover:scale-105 active:scale-95"
              >
                View Projects
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <button
                onClick={() => window.open('/Kajetan Jedrych.pdf', '_blank')}
                className="inline-flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 px-6 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 active:scale-95 backdrop-blur-sm"
              >
                <Download className="w-4 h-4" />
                Download CV
              </button>
            </motion.div>
          </div>

          {/* Right column — photo */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Outer glow ring */}
              <div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(109,40,217,0.4) 0%, rgba(6,182,212,0.3) 100%)',
                  filter: 'blur(28px)',
                  transform: 'scale(1.08)',
                }}
              />

              {/* Decorative corner accent */}
              <div
                className="absolute pointer-events-none"
                style={{
                  top: -16,
                  right: -16,
                  width: 80,
                  height: 80,
                  border: '1.5px solid rgba(139,92,246,0.4)',
                  borderRadius: 12,
                }}
              />
              <div
                className="absolute pointer-events-none"
                style={{
                  bottom: -16,
                  left: -16,
                  width: 80,
                  height: 80,
                  border: '1.5px solid rgba(6,182,212,0.3)',
                  borderRadius: 12,
                }}
              />

              {/* Photo frame with gradient border */}
              <div
                className="relative rounded-3xl overflow-hidden"
                style={{
                  padding: 2,
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.7) 0%, rgba(6,182,212,0.5) 100%)',
                  boxShadow: '0 32px 80px -12px rgba(109,40,217,0.25), 0 0 0 1px rgba(255,255,255,0.04)',
                }}
              >
                <div className="rounded-[22px] overflow-hidden" style={{ width: 'clamp(260px, 32vw, 420px)', aspectRatio: '4/5' }}>
                  <img
                    src="/photo.jpg"
                    alt="Kajetan Jedrych"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center top' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.style.background = 'linear-gradient(135deg, rgba(109,40,217,0.2) 0%, rgba(6,182,212,0.15) 100%)';
                    }}
                  />
                </div>
              </div>

              {/* Floating stat pill */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="absolute -bottom-5 -left-6 bg-slate-900/90 backdrop-blur-md border border-slate-700/60 rounded-2xl px-4 py-2.5 shadow-xl"
              >
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-display mb-0.5">Experience</p>
                <p className="text-slate-100 font-display font-semibold text-sm">5+ Years QA</p>
              </motion.div>

              {/* Floating tech pill */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6, duration: 0.6 }}
                className="absolute -top-5 -right-6 bg-slate-900/90 backdrop-blur-md border border-violet-500/30 rounded-2xl px-4 py-2.5 shadow-xl"
              >
                <p className="text-[10px] text-violet-400 uppercase tracking-widest font-display mb-0.5">Speciality</p>
                <p className="text-slate-100 font-display font-semibold text-sm">AI Testing</p>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-400"
      >
        <span className="text-[10px] tracking-[0.25em] uppercase font-medium font-display">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
